import { Button, Loading, Typography } from '@cobliteam/ui/components';

import type { ChatHistory, ChatSession } from '@cobliteam/api-lib';

import { differenceInMilliseconds } from '@cobliteam/utils/date';

import { useGetChatHistory } from '@cobliteam/hooks';

interface ChatHistoryProps {
  onSetSubmitedUserPrompt: (submitUserPrompt: string) => void;
  onSetAIResponse: (response: ChatHistory) => void;
}

const ChatHistoryModule = ({ onSetSubmitedUserPrompt, onSetAIResponse }: ChatHistoryProps) => {
  const { data: chatSessions, isLoading: isChatHistoryLoading } = useGetChatHistory();

  const groupChatSessionsByDate = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const last30Days = new Date(yesterday);
    last30Days.setDate(last30Days.getDate() - 29);

    const todaySessions: ChatSession[] = [];
    const yesterdaySessions: ChatSession[] = [];
    const last30DaysSessions: ChatSession[] = [];

    chatSessions?.forEach((chatSession) => {
      const chatDate = new Date(chatSession.createdAt);

      if (chatDate.toDateString() === today.toDateString()) {
        todaySessions.push(chatSession);
      } else if (chatDate.toDateString() === yesterday.toDateString()) {
        yesterdaySessions.push(chatSession);
      } else if (chatDate >= last30Days) {
        last30DaysSessions.push(chatSession);
      }
    });

    return [
      { chatSessions: todaySessions, groupByString: 'Hoje' },
      { chatSessions: yesterdaySessions, groupByString: 'Ontem' },
      {
        chatSessions: last30DaysSessions,
        groupByString: 'Últimos 30 dias',
      },
    ];
  };

  const handleChatHistoryClick = (chatSession: ChatSession) => {
    const chatHistory = chatSession.chatHistory;
    const userInput = chatHistory[0].content;

    onSetSubmitedUserPrompt(userInput);
    onSetAIResponse(chatHistory[1]);
  };

  const renderHistoryContent = () => {
    if (isChatHistoryLoading)
      return (
        <div className='flex h-full items-center justify-center'>
          <Loading />
        </div>
      );

    if (chatSessions?.length === 0)
      return (
        <div className='flex h-full items-center justify-center pr-5'>
          <Typography variant='description-title' color='secondary'>
            Sem histórico de perguntas
          </Typography>
        </div>
      );

    return (
      <div className='flex flex-col gap-5 overflow-y-auto'>
        {groupedChatSessions.map((groupChatSession, index) => {
          if (groupChatSession.chatSessions.length === 0) return null;

          return (
            <div key={index} className='flex flex-col gap-2'>
              <div className='pl-2'>
                <Typography variant='caption-bold' color='secondary'>
                  {groupChatSession.groupByString}
                </Typography>
              </div>
              <div className='pr-5'>
                {groupChatSession.chatSessions
                  .sort((a, b) => differenceInMilliseconds(new Date(b.createdAt), new Date(a.createdAt)))
                  .map((chatSession) => {
                    const chatPreview = chatSession.chatHistory[0].content;

                    return (
                      <Button
                        key={chatSession.id}
                        variant='text'
                        color='secondary'
                        full={true}
                        className='justify-start truncate pr-5 pl-2'
                        title={chatPreview}
                        onClick={() => handleChatHistoryClick(chatSession)}
                      >
                        {chatPreview}
                      </Button>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const groupedChatSessions = groupChatSessionsByDate();
  return (
    <div className='flex w-3/12 flex-col gap-5 rounded-lg bg-gray-lightest pt-5 pl-5'>
      <Typography variant='description-title'>Histórico de perguntas</Typography>
      {renderHistoryContent()}
    </div>
  );
};

export default ChatHistoryModule;
